/**
 * ============================================================================
 * useKbEditorial.ts — Hooks do fluxo editorial (draft/review/publish),
 *                     taxonomias, versões e ACL.
 * ----------------------------------------------------------------------------
 * Consome RPCs criadas no F1-Fix Bloco A:
 *   - kb_save_draft, kb_submit_for_review, kb_review_decision
 *   - kb_upsert_taxonomy, kb_assign_taxonomy
 *   - kb_can_read_page (helper de ACL)
 * Também expõe listagem simples de kb_page_versions e kb_taxonomies via select.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.0.0 - Versão inicial (F1-Fix).
 * ============================================================================
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { kbKeys } from "@/hooks/kb/useKb";

// ---------------------------------------------------------------------------
// Save draft (autosave 5s)
// ---------------------------------------------------------------------------
export function useKbSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      page_id: string;
      title?: string | null;
      content_json?: unknown;
      content_markdown?: string | null;
      blocks?: unknown;
    }) => {
      const { data, error } = await supabase.rpc("kb_save_draft", {
        p_page_id: params.page_id,
        p_title: params.title ?? null,
        p_content_json: (params.content_json ?? null) as any,
        p_content_markdown: params.content_markdown ?? null,
        p_blocks: (params.blocks ?? null) as any,
      });
      if (error) throw error;

      // Sincroniza kb_page_links (fundação wiki) — fire-and-forget: nunca
      // trava o salvamento se o sync falhar; log e segue.
      supabase.rpc("kb_sync_page_links", {
        p_page_id: params.page_id,
        p_tenant_hint: null,
      }).then(({ error: syncErr }) => {
        if (syncErr) logger.warn("useKbSaveDraft", "kb_sync_page_links falhou", syncErr);
      });

      return data as string; // version_id
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.page(vars.page_id) });
      qc.invalidateQueries({ queryKey: ["kb", "backlinks"] });
    },
    onError: (err: any) => {
      logger.error("useKbSaveDraft", "erro", err);
    },
  });
}

// ---------------------------------------------------------------------------
// Enviar para revisão
// ---------------------------------------------------------------------------
export function useKbSubmitForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { page_id: string; reviewer_ids?: string[] }) => {
      const { data, error } = await supabase.rpc("kb_submit_for_review", {
        p_page_id: params.page_id,
        p_reviewer_ids: params.reviewer_ids ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.page(vars.page_id) });
      toast({ title: "Enviado para revisão" });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao enviar para revisão", description: err?.message, variant: "destructive" });
    },
  });
}

// ---------------------------------------------------------------------------
// Decisão de revisão (aprovar/recusar)
// ---------------------------------------------------------------------------
export function useKbReviewDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      page_id: string;
      approve: boolean;
      comment?: string | null;
      source_confidence?: number | null;
    }) => {
      const { data, error } = await supabase.rpc("kb_review_decision", {
        p_page_id: params.page_id,
        p_approve: params.approve,
        p_comment: params.comment ?? null,
        p_source_confidence: params.source_confidence ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.page(vars.page_id) });
      toast({ title: vars.approve ? "Página aprovada" : "Página recusada" });
    },
    onError: (err: any) => {
      toast({ title: "Erro na decisão", description: err?.message, variant: "destructive" });
    },
  });
}

// ---------------------------------------------------------------------------
// Listagem de versões (leitura direta autorizada por RLS)
// ---------------------------------------------------------------------------
export function useKbPageVersions(pageId?: string | null) {
  return useQuery({
    queryKey: ["kb", "versions", pageId ?? null],
    enabled: !!pageId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_page_versions")
        .select("id, version_number, status, title, author_user_id, reviewer_user_id, review_comment, source_confidence, published_at, reviewed_at, created_at")
        .eq("page_id", pageId!)
        .order("version_number", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Taxonomias
// ---------------------------------------------------------------------------
export type KbTaxonomyDimension =
  | "department" | "process" | "product" | "module"
  | "rule" | "policy" | "training" | "faq";

export function useKbTaxonomies(dimension?: KbTaxonomyDimension) {
  return useQuery({
    queryKey: ["kb", "taxonomies", dimension ?? "all"],
    queryFn: async () => {
      let q = supabase.from("kb_taxonomies")
        .select("id, dimension, parent_id, name, slug, description, color, position, is_archived")
        .eq("is_archived", false)
        .order("dimension")
        .order("position");
      if (dimension) q = q.eq("dimension", dimension);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

export function useKbUpsertTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      dimension: KbTaxonomyDimension;
      name: string;
      parent_id?: string | null;
      id?: string | null;
      description?: string | null;
      color?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("kb_upsert_taxonomy", {
        p_dimension: params.dimension,
        p_name: params.name,
        p_parent_id: params.parent_id ?? null,
        p_id: params.id ?? null,
        p_description: params.description ?? null,
        p_color: params.color ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb", "taxonomies"] });
      toast({ title: "Taxonomia salva" });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err?.message, variant: "destructive" });
    },
  });
}

export function useKbAssignTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { page_id: string; taxonomy_ids: string[] }) => {
      const { data, error } = await supabase.rpc("kb_assign_taxonomy", {
        p_page_id: params.page_id,
        p_taxonomy_ids: params.taxonomy_ids,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: kbKeys.page(vars.page_id) });
      qc.invalidateQueries({ queryKey: ["kb", "page-taxonomies", vars.page_id] });
    },
  });
}

// Taxonomias atribuídas a uma página
export function useKbPageTaxonomies(pageId?: string | null) {
  return useQuery({
    queryKey: ["kb", "page-taxonomies", pageId ?? null],
    enabled: !!pageId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_page_taxonomies")
        .select("taxonomy_id, kb_taxonomies(id, dimension, name, color)")
        .eq("page_id", pageId!);
      if (error) throw error;
      return (data ?? []).map((r: any) => r.kb_taxonomies).filter(Boolean);
    },
  });
}
